import { MutableRefObject, RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Socket } from 'socket.io-client';
import { ChatMessage, SocketChatSession } from '../types/chat';
import { ScreenType } from '../types/common';
import { createReadReceiptEmitter, ReadReceiptHelper } from './ReadReceiptHelper';

const INTERSECTION_THRESHOLD = 0.5;
const MARK_AS_READ_DELAY_MS = 1500;

interface UseUnreadReadReceiptsOptions {
  screenType: ScreenType;
  isChatVisible: boolean;
  isPinnedToBottom: boolean;
  isPinnedToBottomRef?: MutableRefObject<boolean>;
  activeSessionId: string | null;
  messages?: ChatMessage[];
  sessionMessages?: Record<string, ChatMessage[]>;
  messagesListRef: RefObject<HTMLElement>;
  socket: MutableRefObject<Socket | null>;
  currentSessionRef: MutableRefObject<SocketChatSession | null>;
  getServerNow: () => Date;
  onMessagesRead: (sessionId: string, messageIds: string[], readAt: Date) => void;
}

export const useUnreadReadReceipts = ({
  screenType,
  isChatVisible,
  isPinnedToBottom,
  isPinnedToBottomRef: externalPinnedRef,
  activeSessionId,
  messages,
  sessionMessages,
  messagesListRef,
  socket,
  currentSessionRef,
  getServerNow,
  onMessagesRead
}: UseUnreadReadReceiptsOptions) => {
  const prevMessageIdsRef = useRef<Set<string>>(new Set());
  const prevIsChatVisibleRef = useRef(isChatVisible);
  const initializedSessionRef = useRef<string | null>(null);
  const unreadObserverRegistryRef = useRef<((messageId: string, element: HTMLElement | null) => void) | null>(null);
  const pendingReadTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const pendingMarkAllTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeSessionIdRef = useRef(activeSessionId);
  const isPinnedToBottomRef = useRef(isPinnedToBottom);
  const readEmitterRef = useRef(createReadReceiptEmitter(() => ({
    session: currentSessionRef.current,
    getServerNow
  })));

  activeSessionIdRef.current = activeSessionId;

  const isPinned = () => externalPinnedRef?.current ?? isPinnedToBottomRef.current;

  isPinnedToBottomRef.current = isPinnedToBottom;

  const getActiveMessages = useCallback((): ChatMessage[] => {
    if (sessionMessages && activeSessionId) {
      return sessionMessages[activeSessionId] ?? [];
    }

    return messages ?? [];
  }, [sessionMessages, activeSessionId, messages]);

  const markMessagesRead = useCallback((sessionId: string, messageIds: string[]) => {
    if (messageIds.length === 0 || !sessionId) {
      return;
    }

    messageIds.forEach(cancelPendingReadForMessage);

    const sourceMessages = sessionMessages?.[sessionId] ?? messages ?? [];
    const unreadIds = messageIds.filter(messageId => {
      const message = sourceMessages.find(item => item.id === messageId);
      return message && ReadReceiptHelper.isUnreadFromCounterparty(message, screenType);
    });

    if (unreadIds.length === 0) {
      return;
    }

    const readAt = getServerNow();
    const updatedMessages = ReadReceiptHelper.applyReadTimestamps(sourceMessages, unreadIds, readAt);
    const readMessages = updatedMessages.filter(message => unreadIds.includes(message.id));

    onMessagesRead(sessionId, unreadIds, readAt);
    readEmitterRef.current.queue(socket, readMessages);
  }, [getServerNow, messages, onMessagesRead, screenType, sessionMessages, socket]);

  function cancelPendingReadForMessage(messageId: string) {
    const timeoutId = pendingReadTimeoutsRef.current.get(messageId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      pendingReadTimeoutsRef.current.delete(messageId);
    }
  }

  const cancelAllPendingReads = useCallback(() => {
    pendingReadTimeoutsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
    pendingReadTimeoutsRef.current.clear();

    if (pendingMarkAllTimeoutRef.current) {
      clearTimeout(pendingMarkAllTimeoutRef.current);
      pendingMarkAllTimeoutRef.current = null;
    }
  }, []);

  const markMessagesReadRef = useRef(markMessagesRead);
  markMessagesReadRef.current = markMessagesRead;

  const scheduleMarkMessagesRead = useCallback((sessionId: string, messageIds: string[]) => {
    messageIds.forEach(messageId => {
      if (pendingReadTimeoutsRef.current.has(messageId)) {
        return;
      }

      const timeoutId = setTimeout(() => {
        pendingReadTimeoutsRef.current.delete(messageId);
        markMessagesReadRef.current(sessionId, [messageId]);
      }, MARK_AS_READ_DELAY_MS);

      pendingReadTimeoutsRef.current.set(messageId, timeoutId);
    });
  }, []);

  const scheduleMarkMessagesReadRef = useRef(scheduleMarkMessagesRead);
  scheduleMarkMessagesReadRef.current = scheduleMarkMessagesRead;

  const markAllUnreadInActiveSession = useCallback((options?: { requirePinned?: boolean }) => {
    if (!isChatVisible || !activeSessionId) {
      return;
    }

    const requirePinned = options?.requirePinned ?? true;

    if (pendingMarkAllTimeoutRef.current) {
      clearTimeout(pendingMarkAllTimeoutRef.current);
    }

    pendingMarkAllTimeoutRef.current = setTimeout(() => {
      pendingMarkAllTimeoutRef.current = null;

      if (requirePinned && !isPinned()) {
        return;
      }

      const sessionId = activeSessionIdRef.current;
      if (!sessionId) {
        return;
      }

      const unreadIds = ReadReceiptHelper.getUnreadCounterpartyMessages(
        sessionMessages?.[sessionId] ?? messages ?? [],
        screenType
      ).map(message => message.id);

      unreadIds.forEach(cancelPendingReadForMessage);
      markMessagesReadRef.current(sessionId, unreadIds);
    }, MARK_AS_READ_DELAY_MS);
  }, [activeSessionId, isChatVisible, messages, screenType, sessionMessages]);

  const unreadCountsBySession = useMemo(() => {
    if (sessionMessages) {
      return Object.entries(sessionMessages).reduce<Record<string, number>>((sessionCounts, [sessionId, sessionMsgs]) => {
        sessionCounts[sessionId] = ReadReceiptHelper.countUnreadMessages(sessionMsgs, screenType);
        return sessionCounts;
      }, {});
    }

    const count = ReadReceiptHelper.countUnreadMessages(messages ?? [], screenType);
    if (!activeSessionId) {
      return {};
    }

    return { [activeSessionId]: count };
  }, [sessionMessages, messages, screenType, activeSessionId]);

  const totalUnreadCount = useMemo(
    () => Object.values(unreadCountsBySession).reduce((total, count) => total + count, 0),
    [unreadCountsBySession]
  );

  const notificationTotalUnreadCount = useMemo(() => {
    if (!isChatVisible || !isPinnedToBottom || !activeSessionId) {
      return totalUnreadCount;
    }

    const activeUnreadCount = unreadCountsBySession[activeSessionId] ?? 0;
    return Math.max(0, totalUnreadCount - activeUnreadCount);
  }, [activeSessionId, isChatVisible, isPinnedToBottom, totalUnreadCount, unreadCountsBySession]);

  const firstUnreadMessageId = useMemo(() => {
    if (!isChatVisible || !activeSessionId) {
      return null;
    }

    return ReadReceiptHelper.findFirstUnreadMessageId(getActiveMessages(), screenType);
  }, [activeSessionId, getActiveMessages, isChatVisible, screenType, messages, sessionMessages]);

  const suppressActiveSessionIndicators = isChatVisible && Boolean(activeSessionId) && isPinnedToBottom;

  const displayUnreadCountsBySession = useMemo(() => {
    if (!suppressActiveSessionIndicators || !activeSessionId) {
      return unreadCountsBySession;
    }

    return {
      ...unreadCountsBySession,
      [activeSessionId]: 0
    };
  }, [unreadCountsBySession, suppressActiveSessionIndicators, activeSessionId]);

  const displayFirstUnreadMessageId = useMemo(() => {
    if (suppressActiveSessionIndicators) {
      return null;
    }

    return firstUnreadMessageId;
  }, [firstUnreadMessageId, suppressActiveSessionIndicators]);

  useEffect(() => {
    readEmitterRef.current = createReadReceiptEmitter(() => ({
      session: currentSessionRef.current,
      getServerNow
    }));
  }, [currentSessionRef, getServerNow]);

  useEffect(() => () => {
    readEmitterRef.current.dispose(socket);
    cancelAllPendingReads();
  }, [socket, cancelAllPendingReads]);

  useEffect(() => {
    cancelAllPendingReads();
  }, [activeSessionId, isChatVisible, cancelAllPendingReads]);

  useEffect(() => {
    const wasHidden = !prevIsChatVisibleRef.current;
    prevIsChatVisibleRef.current = isChatVisible;

    if (wasHidden && isChatVisible && activeSessionId) {
      markAllUnreadInActiveSession({ requirePinned: false });
    }
  }, [isChatVisible, activeSessionId, markAllUnreadInActiveSession]);

  useEffect(() => {
    if (initializedSessionRef.current !== activeSessionId) {
      initializedSessionRef.current = activeSessionId;
      prevMessageIdsRef.current = new Set(getActiveMessages().map(message => message.id));
    }
  }, [activeSessionId, getActiveMessages]);

  useLayoutEffect(() => {
    if (!isChatVisible || !activeSessionId || !isPinned()) {
      prevMessageIdsRef.current = new Set(getActiveMessages().map(message => message.id));
      return;
    }

    const activeMessages = getActiveMessages();
    const currentIds = new Set(activeMessages.map(message => message.id));
    const newMessages = activeMessages.filter(message => !prevMessageIdsRef.current.has(message.id));
    prevMessageIdsRef.current = currentIds;

    const newUnreadIds = newMessages
      .filter(message => ReadReceiptHelper.isUnreadFromCounterparty(message, screenType))
      .map(message => message.id);

    if (newUnreadIds.length > 0) {
      markMessagesReadRef.current(activeSessionId, newUnreadIds);
    }
  }, [
    activeSessionId,
    getActiveMessages,
    isChatVisible,
    isPinnedToBottom,
    messages,
    screenType,
    sessionMessages
  ]);

  useEffect(() => {
    const scrollRoot = messagesListRef.current;
    if (!scrollRoot || !isChatVisible || !activeSessionId) {
      return;
    }

    const observedElements = new Map<string, HTMLElement>();

    const handleIntersection: IntersectionObserverCallback = (entries) => {
      entries.forEach(entry => {
        const messageId = entry.target.getAttribute('data-message-id');
        if (!messageId) {
          return;
        }

        if (!entry.isIntersecting || entry.intersectionRatio < INTERSECTION_THRESHOLD) {
          cancelPendingReadForMessage(messageId);
          return;
        }

        if (isPinned()) {
          return;
        }

        const sessionId = activeSessionIdRef.current;
        if (!sessionId) {
          return;
        }

        scheduleMarkMessagesReadRef.current(sessionId, [messageId]);
      });
    };

    const observer = new IntersectionObserver(handleIntersection, {
      root: scrollRoot,
      threshold: INTERSECTION_THRESHOLD
    });

    const registerElement = (messageId: string, element: HTMLElement | null) => {
      const existing = observedElements.get(messageId);
      if (existing) {
        observer.unobserve(existing);
        observedElements.delete(messageId);
      }

      if (!element) {
        return;
      }

      observedElements.set(messageId, element);
      observer.observe(element);
    };

    unreadObserverRegistryRef.current = registerElement;

    return () => {
      unreadObserverRegistryRef.current = null;
      observer.disconnect();
      observedElements.clear();
    };
  }, [activeSessionId, isChatVisible, messagesListRef]);

  const registerUnreadMessageRef = useCallback((messageId: string, isUnread: boolean) => (element: HTMLDivElement | null) => {
    unreadObserverRegistryRef.current?.(messageId, isUnread ? element : null);
  }, []);

  return {
    unreadCountsBySession: displayUnreadCountsBySession,
    totalUnreadCount,
    notificationTotalUnreadCount,
    firstUnreadMessageId: displayFirstUnreadMessageId,
    registerUnreadMessageRef,
    markAllUnreadInActiveSession
  };
};
